import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import CommonBreadcrumb from "../../components/CommonBreadcrumb";
import {
  Button,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import SearchBar from "../../components/SearchBar";
import { usePermission } from "../../hooks/usePermission";
import Can from "../../components/Can";
import SwitchToggle from "../../components/SwitchToggle";
import { useGetBlogsQuery, useDeleteBlogMutation, useUpdateBlogMutation } from "../../service/blogs/api";
import Pagination from "../../components/Panigation";
import moment from "moment";

const ManageBlogs = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canEdit = hasPermission("Blog Management", "isEdit");
  
  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState<string>("default");
  const [page, setPage] = useState(1);

  const queryParams: any = { page, limit: 10 };
  if (searchTerm) {
    queryParams.search = searchTerm;
  }
  if (status !== "default" && status !== "all") {
    queryParams.isActive = status === "active" ? true : false;
  }

  const { data, isLoading } = useGetBlogsQuery(queryParams);
  const [deleteBlog, { isLoading: isDeleting }] = useDeleteBlogMutation();
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const blogs = data?.data?.data || [];
  const paginator = data?.data?.paginator || {};

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteBlog(deleteId).unwrap();
      toast.success("Blog deleted successfully");
      setDeleteId(null);
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete blog");
    }
  };

  const [updateBlog, { isLoading: isUpdating }] = useUpdateBlogMutation();

  const handleToggleStatus = async (blog: any) => {
    try {
      await updateBlog({ id: blog._id, isActive: !blog.isActive }).unwrap();
      toast.success(
        `Blog ${!blog.isActive ? "enabled" : "disabled"} successfully`,
        { autoClose: 2000 }
      );
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to update blog status");
    }
  };

  const truncateTitle = (title: string, wordCount: number = 5): string => {
    const words = title.split(' ');
    if (words.length > wordCount) {
      return words.slice(0, wordCount).join(' ') + '...';
    }
    return title;
  };

  return (
    <>
      {/* PAGE TITLE */}
      <CommonBreadcrumb
        title="Blogs Management (إدارة المدونات)"
        breadcrumbItems={[
          { label: "Dashboard", onClick: () => navigate("/dashboard") },
          { label: "Blogs Management" },
        ]}
      />

      <div className="cards">
        {/* TABLE HEADER */}
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setSearchTerm}
              placeholder="Search..."
            />
            <div className="form">
              <div className="control_group">
                <Select
                  fullWidth
                  hiddenLabel
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="default" disabled>Select Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </Select>
              </div>
            </div>
          </div>
          <div className="right_s">
            <Can module="Blog Management" action="isAdd" isDisabledMode={true}>
              <Button
                className="btn btn_primary"
                onClick={() => navigate("/manage-blogs-listings/add")}
              >
                <AddIcon /> Add Blog (أضف مدونة)
              </Button>
            </Can>
          </div>
        </div>

        {/* TABLE */}
        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sr. No</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Author Name</TableCell>
                <TableCell>Published Date</TableCell>
                <TableCell>Disable/Enable</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : blogs.length > 0 ? (
                blogs.map((blog: any, index: number) => (
                  <TableRow hover key={blog._id}>
                    {/* SR NO */}
                    <TableCell>{(page - 1) * 10 + index + 1}</TableCell>

                    {/* DETAIL (Image and Title) */}
                    <TableCell>
                      <div className="user_block">
                        <figure className="no_radius">
                          <img src={blog.blogBannerImage} alt={blog.blogTitle} />
                        </figure>
                        <p>
                          <Tooltip title={blog.blogTitle} arrow>
                            <b>{truncateTitle(blog.blogTitle)}</b>
                          </Tooltip>
                        </p>
                      </div>
                    </TableCell>
                    {/* AUTHOR NAME */}
                    <TableCell>{blog.authorName}</TableCell>

                    {/* PUBLISHED DATE */}
                    <TableCell>{moment(blog.createdAt).format("DD-MMM-YYYY")}</TableCell>

                    {/* STATUS */}
                    <TableCell>
                      {canEdit ? (
                        <SwitchToggle 
                          checked={blog.isActive} 
                          onChange={() => handleToggleStatus(blog)}
                        />
                      ) : (
                        <span className={`status_btn ${blog.isActive ? "c_dark_info" : "c_dark_warning"}`}>
                          {blog.isActive ? "Enabled" : "Disabled"}
                        </span>
                      )}
                    </TableCell>

                    {/* ACTIONS */}
                    <TableCell>
                      <div className="table_actions v2">
                        <Tooltip title="View" arrow>
                          <IconButton onClick={() => navigate(`/manage-blogs-listings/detail/${blog._id}`)}>
                            <img
                              src="/static/images/eye_icon.svg"
                              alt="View"
                            />
                          </IconButton>
                        </Tooltip>
                        <Can module="Blog Management" action="isEdit" isDisabledMode={true}>
                          <Tooltip title="Edit" arrow>
                            <IconButton onClick={() => navigate(`/manage-blogs-listings/edit/${blog._id}`)}>
                              <img
                                src="/static/images/edit2_icon.svg"
                                alt="Edit"
                              />
                            </IconButton>
                          </Tooltip>
                        </Can>
                        <Can module="Blog Management" action="isDelete" isDisabledMode={true}>
                          <Tooltip title="Delete" arrow>
                            <IconButton onClick={() => setDeleteId(blog._id)}>
                              <img
                                src="/static/images/trash2_icon.svg"
                                alt="Delete"
                              />
                            </IconButton>
                          </Tooltip>
                        </Can>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No Blogs Found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {paginator?.pageCount > 1 && (
          <Pagination
            module={blogs}
            page={page}
            onPageChange={handlePageChange}
            totalPages={paginator.pageCount}
            totalCount={paginator.itemCount}
            setPage={setPage}
          />
        )}
      </div>

      <Dialog
        open={Boolean(deleteId)}
        onClose={() => setDeleteId(null)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this blog? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)} color="primary" disabled={isDeleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" disabled={isDeleting} variant="contained">
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManageBlogs;
